import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ENDPOINTS } from "../../api/endpoints";
import { getCompanySalesReps } from "../../services/userService";
import { updateNotes } from "../../services/zohoService";
import { useMessageApi } from "../../shared/providers/MessageProvider";
import { getStatusClass } from "../../helpers/merchantHelpers";
import { Select, Input, DatePicker } from "antd";
import dayjs from "dayjs";
import api from "../../api/config";
import iconEditGreen from "../../assets/images/icon-edit-green.png";
import iconAddNotes from "../../assets/images/icon-add-notes.svg";
import iconSaveGreen from "../../assets/images/icon-save-green.svg";
import iconCancelRed from "../../assets/images/icon-cancel-red.svg";
import "./BusinessModal.scss";

const normalizeValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

// Initial state for form management
const initialState = {
  merchantName: "",
  merchantPhone: "",
  merchantEmail: "",
  merchantAddress: "",
  dateSubmitted: "",
  lastUpdated: "",
  accountExec: "",
  gatewayId: "",
  salesRep: "",
  startRep: "",
  newNote: "",
  isLoadingAccountExec: false,
  isEditingAppInfo: false,
  isEditingMerchantInfo: false,
  isAddingNote: false,
  accountExecOptions: [],
  startRepOptions: [],
  isLoadingAgents: false,
};

/**
 * Simple Tooltip Component
 * Displays a tooltip on hover with configurable delay
 * 
 * @param {React.ReactNode} children - Element to attach tooltip to
 * @param {string} text - Tooltip text content
 * @param {number} delay - Delay before showing tooltip (default: 500ms)
 */
const SimpleTooltip = ({ children, text, delay = 500 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Handle mouse enter with delay
  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  // Handle mouse leave - hide tooltip immediately
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!containerRef.current) return {};
    
    const rect = containerRef.current.getBoundingClientRect();
    return {
      top: rect.top - 30,
      left: rect.left + (rect.width / 2),
    };
  };

  return (
    <div 
      ref={containerRef}
      className="simple-tooltip-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div 
          className="simple-tooltip"
          style={getTooltipStyle()}
        >
          {text}
          <div className="simple-tooltip-arrow"></div>
        </div>
      )}
    </div>
  );
};

/**
 * BusinessModal Component
 * A comprehensive modal for displaying and editing merchant business information
 * Features conditional field display based on company type and inline editing capabilities
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.display - Controls modal visibility
 * @param {Array} props.modalMerchant - Array containing merchant data object
 * @param {Function} props.handleOutsideClick - Handler for outside modal clicks
 * @param {Function} props.refreshInfo - Function to refresh merchant information
 * @param {string} props.company - Company name to determine field labels and behavior
 * @param {Object} props.userInfo - User information object containing email and name
 */
const BusinessModal = ({
  display,
  modalMerchant,
  handleOutsideClick,
  refreshInfo,
  company,
  userInfo,
  canEditAllLeads = false,
}) => {
  const message = useMessageApi();
  // Centralized state management for better performance
  const [state, setState] = useState(initialState);

  // Company type detection for conditional rendering
  const isCybersource = company?.toLowerCase().includes('cybersource');

  const userIdentifiers = useMemo(() => {
    if (!userInfo) return [];
    const identifiers = [
      userInfo?.name,
      userInfo?.email,
      userInfo?.full_name,
      userInfo?.fullName,
      userInfo?.display_name,
      userInfo?.displayName
    ]
      .map(normalizeValue)
      .filter(Boolean);
    return Array.from(new Set(identifiers));
  }, [userInfo]);

  const assignedOwners = useMemo(() => {
    if (!modalMerchant?.length) return [];
    const merchantData = modalMerchant[0] || {};
    const potentialOwners = [
      merchantData?.Sales_Agent,
      merchantData?.Account_Executive,
      merchantData?.Account_Rep,
      merchantData?.Submitting_Agent
    ]
      .map(normalizeValue)
      .filter(Boolean);
    return Array.from(new Set(potentialOwners));
  }, [modalMerchant]);

  const isLeadAssignedToUser = useMemo(() => {
    if (userIdentifiers.length === 0 || assignedOwners.length === 0) {
      return false;
    }
    return userIdentifiers.some((identifier) =>
      assignedOwners.includes(identifier)
    );
  }, [userIdentifiers, assignedOwners]);

  const canEditLead = useMemo(
    () => canEditAllLeads || isLeadAssignedToUser,
    [canEditAllLeads, isLeadAssignedToUser]
  );

  const assignedOwnerLabel = useMemo(() => {
    if (!modalMerchant?.length) return null;
    const merchantData = modalMerchant[0];
    if (isCybersource && merchantData?.Account_Executive) {
      return merchantData.Account_Executive;
    }
    return (
      merchantData?.Sales_Agent ||
      merchantData?.Account_Executive ||
      merchantData?.Account_Rep ||
      merchantData?.Submitting_Agent ||
      null
    );
  }, [modalMerchant, isCybersource]);

  const showEditPermissionWarning = useCallback(() => {
    message.warning("You can only edit leads assigned to you.", 2.5);
  }, [message]);

  const ensureCanEditLead = useCallback(() => {
    if (canEditLead) {
      return true;
    }
    showEditPermissionWarning();
    return false;
  }, [canEditLead, showEditPermissionWarning]);

  /**
   * Reset state when modal closes or merchant changes
   */
  useEffect(() => {
    if (!display) {
      // Reset all edit-related state when modal closes
      setState(initialState);
    }
  }, [display]);

  // Unified fetch status for dropdowns
  const agentsFetchStatusRef = useRef({
    account: { fetching: false, loaded: false },
    sales: { fetching: false, loaded: false }
  });

  const loadAgentsFor = useCallback(async (type) => {
    const status = agentsFetchStatusRef.current[type];
    if (status.fetching) return;
    if (status.loaded && ((type === 'account' && state.accountExecOptions.length) || (type === 'sales' && state.startRepOptions.length))) return;

    agentsFetchStatusRef.current[type].fetching = true;
    setState(prev => ({ ...prev, isLoadingAgents: true }));
    try {
      const sourceCompany = type === 'account' ? 'CyberSource' : company;
      const agents = await getCompanySalesReps(sourceCompany);
      setState(prev => ({
        ...prev,
        ...(type === 'account'
          ? { accountExecOptions: agents.map(agent => ({ value: agent.name, label: agent.name })) }
          : { startRepOptions: agents.map(agent => ({ value: agent.name, label: agent.name })) }),
        isLoadingAgents: false
      }));
      agentsFetchStatusRef.current[type].loaded = true;
    } catch (error) {
      console.error(`Error loading ${type === 'account' ? 'account executives' : 'sales reps'}:`, error);
      message.error(`Failed to load ${type === 'account' ? 'account executives' : 'sales reps'}`, 2.5);
      setState(prev => ({ ...prev, isLoadingAgents: false }));
    } finally {
      agentsFetchStatusRef.current[type].fetching = false;
    }
  }, [state.accountExecOptions.length, state.startRepOptions.length, company, message]);

  const handleOpenAccountDropdown = useCallback((open) => {
    if (open && !agentsFetchStatusRef.current.account.loaded) {
      setState(prev => ({ ...prev, isLoadingAgents: true, accountExecOptions: [] }));
      loadAgentsFor('account');
    }
  }, [loadAgentsFor]);

  const handleOpenSalesDropdown = useCallback((open) => {
    if (open && !agentsFetchStatusRef.current.sales.loaded) {
      setState(prev => ({ ...prev, isLoadingAgents: true, startRepOptions: [] }));
      loadAgentsFor('sales');
    }
  }, [loadAgentsFor]);

  const filterOption = useCallback((input, option) => {
    const label = (option?.label || '').toString().toLowerCase();
    return label.includes(input.toLowerCase());
  }, []);

  /**
   * Format date string for display
   * @param {string|Date} date - Date to format
   * @returns {string} Formatted date or "-" if invalid
   */
  const formatDate = useCallback((date) => {
    return date ? new Date(date).toLocaleDateString() : "-";
  }, []);

  /**
   * Handle form input changes
   * @param {Event} e - Input change event
   */
  const handleChange = useCallback((e) => {
    setState(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);
  
  /**
   * Truncate text with ellipsis if too long
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length before truncation
   * @returns {string} Truncated text with ellipsis
   */
  const truncateText = useCallback((text, maxLength = 50) => {
    if (!text) return "No Company Name";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  }, []);

  // Early return guard replaced with boolean to avoid conditional hooks
  const shouldHideModal = !display || !modalMerchant?.length;

  // Safe merchant object (prevents null access when hidden)
  const merchant = (modalMerchant && modalMerchant.length ? modalMerchant[0] : {});
  
  /**
   * Get merchant address for display
   * @returns {string} Merchant street address or empty string
   */
  const getMerchantAddress = () => {
    return merchant.Street_Address || "";
  };

  // Toggle add note mode
  const toggleAddNote = () => {
    if (!ensureCanEditLead()) {
      return;
    }
    setState(prev => ({
      ...prev,
      isAddingNote: !prev.isAddingNote,
      newNote: ""
    }));
  };

  // Save new note with timestamp
  const saveNote = async () => {
    if (!ensureCanEditLead()) {
      return;
    }
    try {
      if (!state.newNote.trim()) {
        message.error("Please enter a note", 2.5);
        return;
      }

      if (state.newNote.length > 500) {
        message.error("Note cannot exceed 500 characters", 2.5);
        return;
      }

      setState(prev => ({ ...prev, isLoadingAccountExec: true }));

      // Create timestamp in MM/DD/YYYY format
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const year = currentDate.getFullYear();
      const timestamp = `${month}/${day}/${year}`;
      
      const response = await updateNotes({
        zohoID: merchant.id,
        notes: state.newNote.trim(),
        addTimestamp: true
      });
      
      if (response && response.success) {
        // Update local merchant data with timestamped note
        const newNoteText = `${timestamp} - ${state.newNote.trim()}`;
        merchant.Notes1 = merchant.Notes1 ? `${merchant.Notes1}\n${newNoteText}` : newNoteText;
        
        message.success("Note added successfully!", 2.5);
        
        // Small delay to ensure backend update is complete, then refresh
        setTimeout(() => {
          refreshInfo(merchant.id);
        }, 500);
      } else {
        throw new Error(response?.error || "Failed to update notes");
      }

      setState(prev => ({ 
        ...prev, 
        isAddingNote: false,
        isLoadingAccountExec: false,
        newNote: ""
      }));
    } catch (error) {
      console.error("Error adding note:", error);
      message.error("Could not add note. Please try again.", 2.5);
      setState(prev => ({ ...prev, isLoadingAccountExec: false }));
    }
  };

  /**
   * Toggle Application Info edit mode
   * Populates form with current merchant values when entering edit mode
   * Resets form when canceling edit mode
   */
  const toggleAppInfoEdit = () => {
    if (!ensureCanEditLead()) {
      return;
    }
    if (!state.isEditingAppInfo) {
      // Entering edit mode - populate fields based on company type
      setState(prev => ({
        ...prev,
        isEditingAppInfo: true,
        dateSubmitted: merchant.Created_Time?.split('T')[0] || "",
        lastUpdated: merchant.Last_Activity_Time?.split('T')[0] || "",
        accountExec: merchant.Account_Executive || "",
        gatewayId: merchant.Gateway_ID || "",
        salesRep: merchant.Sales_Agent || "",
        startRep: merchant.Account_Rep || ""
      }));
    } else {
      // Exiting edit mode - clear fields
      setState(prev => ({
        ...prev,
        isEditingAppInfo: false,
        dateSubmitted: "",
        lastUpdated: "",
        accountExec: "",
        gatewayId: "",
        salesRep: "",
        startRep: ""
      }));
    }
  };

  /**
   * Save Application Info changes to backend
   * Compares old and new values, sends update request if changes exist
   * Uses unified API endpoint for business info updates
   */
  const saveAppInfo = async () => {
    if (!ensureCanEditLead()) {
      return;
    }
    try {
      setState(prev => ({ ...prev, isLoadingAccountExec: true }));

      // Prepare old values from current merchant data
      const oldValues = {
        createdTime: merchant.Created_Time?.split('T')[0] || "",
        lastActivityTime: merchant.Last_Activity_Time?.split('T')[0] || "",
        accountExecutive: merchant.Account_Executive || "",
        gatewayId: merchant.Gateway_ID || "",
        salesAgent: merchant.Sales_Agent || "",
        accountRep: merchant.Account_Rep || ""
      };

      // Prepare new values from form state
      const newValues = {
        createdTime: state.dateSubmitted || "",
        lastActivityTime: state.lastUpdated || "",
        accountExecutive: state.accountExec || "",
        gatewayId: state.gatewayId || "",
        salesAgent: state.salesRep || "",
        accountRep: state.startRep || ""
      };

      // Check if there are any meaningful changes
      const hasChanges = Object.keys(newValues).some(key => 
        newValues[key] !== oldValues[key] && newValues[key] !== ""
      );

      if (!hasChanges) {
        message.info("No changes detected", 2.5);
        setState(prev => ({
          ...prev,
          isEditingAppInfo: false,
          isLoadingAccountExec: false,
          dateSubmitted: "",
          lastUpdated: "",
          accountExec: "",
          gatewayId: "",
          salesRep: "",
          startRep: ""
        }));
        return;
      }

      // Send update request to unified API endpoint
      const payload = {
        zohoID: merchant.id,
        section: "application",
        oldValues,
        newValues,
        updatedBy: userInfo?.email || userInfo?.name || "Unknown User"
      };

      const response = await api.post(ENDPOINTS.UPDATE_BUSINESS_INFO, payload);
      
      if (response.data.success) {
        if (response.data.summary.emailSent) {
          message.success("Changes saved! Admin has been notified to update Zoho manually.", 2.5);
        } else {
          message.warning("Changes saved! Admin notification failed - please verify email configuration.", 2.5);
        }
      }

      // Reset form state after successful save
      setState(prev => ({ 
        ...prev, 
        isEditingAppInfo: false,
        isLoadingAccountExec: false,
        dateSubmitted: "",
        lastUpdated: "",
        accountExec: "",
        gatewayId: "",
        salesRep: "",
        startRep: ""
      }));
    } catch (error) {
      console.error("Error updating Application Info:", error);
      message.error("Could not update Application Info. Please try again.", 2.5);
      setState(prev => ({ ...prev, isLoadingAccountExec: false }));
    }
  };

  // Toggle Merchant Info edit mode
  const toggleMerchantInfoEdit = () => {
    if (!ensureCanEditLead()) {
      return;
    }
    if (!state.isEditingMerchantInfo) {
      // Entering edit mode - populate fields
      const currentAddress = getMerchantAddress();
      const addressToSet = currentAddress || "";

      setState(prev => ({
        ...prev,
        isEditingMerchantInfo: true,
        merchantName: merchant.Full_Name || "",
        merchantPhone: merchant.Phone || "",
        merchantEmail: merchant.Email || "",
        merchantAddress: addressToSet
      }));
    } else {
      // Exiting edit mode - clear fields
      setState(prev => ({
        ...prev,
        isEditingMerchantInfo: false,
        merchantName: "",
        merchantPhone: "",
        merchantEmail: "",
        merchantAddress: ""
      }));
    }
  };



  // Save Merchant Info
  const saveMerchantInfo = async () => {
    if (!ensureCanEditLead()) {
      return;
    }
    try {
      setState(prev => ({ ...prev, isLoadingAccountExec: true }));

      // Prepare old values
      const oldValues = {
        fullName: merchant.Full_Name || "",
        phone: merchant.Phone || "",
        email: merchant.Email || "",
        address: getMerchantAddress() || ""
      };

      // Prepare new values
      const newValues = {
        fullName: state.merchantName || "",
        phone: state.merchantPhone || "",
        email: state.merchantEmail || "",
        address: state.merchantAddress || ""
      };

      // Check if there are any changes
      const hasChanges = Object.keys(newValues).some(key => 
        newValues[key] !== oldValues[key] && newValues[key] !== ""
      );

      if (!hasChanges) {
        message.info("No changes detected", 2.5);
        setState(prev => ({
          ...prev,
          isEditingMerchantInfo: false,
          isLoadingAccountExec: false,
          merchantName: "",
          merchantPhone: "",
          merchantEmail: "",
          merchantAddress: ""
        }));
        return;
      }

      // Use the unified API endpoint for Merchant Info updates
      const payload = {
        zohoID: merchant.id,
        section: "merchant",
        oldValues,
        newValues,
        updatedBy: userInfo?.email || userInfo?.name || "Unknown User"
      };

      const response = await api.post(ENDPOINTS.UPDATE_BUSINESS_INFO, payload);
      
      if (response.data.success) {        
        if (response.data.summary.emailSent) {
          message.success("Changes saved! Admin has been notified to update Zoho manually.", 2.5);
        } else {
          message.warning("Changes saved! Admin notification failed - please verify email configuration.", 2.5);
        }
      }

      setState(prev => ({
        ...prev,
        isEditingMerchantInfo: false,
        isLoadingAccountExec: false,
        merchantName: "",
        merchantPhone: "",
        merchantEmail: "",
        merchantAddress: ""
      }));
    } catch (error) {
      console.error("Error updating Merchant Info:", error);
      message.error("Could not update Merchant Info. Please try again.", 2.5);
      setState(prev => ({ ...prev, isLoadingAccountExec: false }));
    }
  };

  useEffect(() => {
    if (!canEditLead) {
      setState(prev => ({
        ...prev,
        isEditingAppInfo: false,
        isEditingMerchantInfo: false,
        isAddingNote: false,
        newNote: ""
      }));
    }
  }, [canEditLead]);

  useEffect(() => {
    agentsFetchStatusRef.current = {
      account: { fetching: false, loaded: false },
      sales: { fetching: false, loaded: false }
    };
    
  }, [company]);

  useEffect(() => {
    if (!display) {
      agentsFetchStatusRef.current = {
        account: { fetching: false, loaded: false },
        sales: { fetching: false, loaded: false }
      };
    }
  }, [display]);

  return shouldHideModal ? null : (
    <div
      className="modal fade show businessModal"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
      aria-labelledby="businessModalLabel"
      aria-modal="true"
      role="dialog"
      onClick={handleOutsideClick}
    >
      <div
        className="modal-dialog modal-dialog-custom"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header border-0 align-items-center">
            <div className="d-flex align-items-center gap-3">
              <h1 className="modal-title mb-0" id="businessModalLabel" title={merchant.Company || "No Company Name"}>
                {truncateText(merchant.Company)}
              </h1>
              {merchant.Lead_Status && (
                <span className={`badge cBadge ${getStatusClass(merchant.Lead_Status)}`}>
                  {merchant.Lead_Status}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleOutsideClick}
            />
          </div>
          <div className="modal-body">
            {!canEditLead && (
              <div className="alert alert-info permission-alert mb-4 py-2 px-3">
                You can view this lead, but only leads assigned to you
                {assignedOwnerLabel ? ` (currently assigned to ${assignedOwnerLabel})` : ""}
                {" "}can be edited.
              </div>
            )}
            {/* Application Info Section */}
            <div className="info-section mb-4">
              <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                <h5 className="section-title mb-0">
                  Application Info
                  {!state.isEditingAppInfo && canEditLead && (
                    <SimpleTooltip text="Click to edit Application Info">
                      <img
                        src={iconEditGreen}
                        alt="edit"
                        className="edit-icon ms-2 icon-button-pointer"
                        onClick={toggleAppInfoEdit}
                      />
                    </SimpleTooltip>
                  )}
                </h5>
                {state.isEditingAppInfo && (
                  <div className="d-flex gap-2">
                    <SimpleTooltip text="Save Changes">
                      <img
                        src={iconSaveGreen}
                        alt="save"
                        onClick={saveAppInfo}
                        className={`icon-button-medium ${state.isLoadingAccountExec ? 'icon-button-disabled' : 'icon-button-pointer'}`}
                      />
                    </SimpleTooltip>
                    <SimpleTooltip text="Cancel">
                      <img
                        src={iconCancelRed}
                        alt="cancel"
                        onClick={state.isLoadingAccountExec ? null : toggleAppInfoEdit}
                        className={`icon-button-medium ${state.isLoadingAccountExec ? 'icon-button-disabled' : 'icon-button-pointer'}`}
                      />
                    </SimpleTooltip>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-2 col-6 mb-3">
                  <label className="field-label">Date Submitted</label>
                  {state.isEditingAppInfo ? (
                    <DatePicker
                      className="w-100 agent-picker"
                      format="YYYY-MM-DD"
                      value={state.dateSubmitted ? dayjs(state.dateSubmitted, "YYYY-MM-DD") : null}
                      onChange={(d) => setState(prev => ({ ...prev, dateSubmitted: d ? d.format("YYYY-MM-DD") : "" }))}
                    />
                  ) : (
                    <div className="field-value">
                      <b>{formatDate(merchant.Created_Time)}</b>
                    </div>
                  )}
                </div>
                <div className="col-md-2 col-6 mb-3">
                  <label className="field-label">Last Updated</label>
                  {state.isEditingAppInfo ? (
                    <DatePicker
                      className="w-100 agent-picker"
                      format="YYYY-MM-DD"
                      value={state.lastUpdated ? dayjs(state.lastUpdated, "YYYY-MM-DD") : null}
                      onChange={(d) => setState(prev => ({ ...prev, lastUpdated: d ? d.format("YYYY-MM-DD") : "" }))}
                    />
                  ) : (
                    <div className="field-value">
                      <b>{formatDate(merchant.Last_Activity_Time)}</b>
                    </div>
                  )}
                </div>
                
                {/* Show Account Exec for CyberSource, Sales Rep for Authorize.net */}
                {isCybersource ? (
                  <div className="col-md-3 col-6 mb-3">
                    <label className="field-label d-flex align-items-center gap-2">
                      Account Ex
                    </label>
                    {state.isEditingAppInfo ? (
                      <Select
                        className="w-100"
                        size="large"
                        value={state.accountExec ? state.accountExec : null}
                        onChange={(val) => setState(prev => ({ ...prev, accountExec: val || "" }))}
                        onDropdownVisibleChange={handleOpenAccountDropdown}
                        placeholder={state.isLoadingAgents ? 'Loading agents…' : 'Account Executive'}
                        allowClear={false}
                        showSearch
                        filterOption={filterOption}
                        optionFilterProp="label"
                        loading={state.isLoadingAgents}
                        suffixIcon={state.isLoadingAgents ? <span className="spinner-border spinner-border-sm text-success" /> : undefined}
                        dropdownMatchSelectWidth={false}
                         virtual={true}
                        options={state.accountExecOptions}
                        notFoundContent={state.isLoadingAgents ? (
                          <div className="d-flex justify-content-center align-items-center py-2" style={{ color: '#198754' }}>
                            <span className="spinner-border spinner-border-sm text-success me-2" role="status" />
                            Loading agents…
                          </div>
                        ) : null}
                      />
                    ) : (
                      <div className="field-value">
                        <b>{merchant.Account_Executive || "-"}</b>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="col-md-3 col-6 mb-3">
                    <label className="field-label d-flex align-items-center gap-2">
                      Sales Rep
                    </label>
                    {state.isEditingAppInfo ? (
                      <Select
                        className="w-100"
                        size="large"
                        value={state.salesRep ? state.salesRep : null}
                        onChange={(val) => setState(prev => ({ ...prev, salesRep: val || "" }))}
                        onDropdownVisibleChange={handleOpenSalesDropdown}
                        placeholder={state.isLoadingAgents ? 'Loading agents…' : 'Sales Rep'}
                        allowClear={false}
                        showSearch
                        filterOption={filterOption}
                        optionFilterProp="label"
                        loading={state.isLoadingAgents}
                        suffixIcon={state.isLoadingAgents ? <span className="spinner-border spinner-border-sm text-success" /> : undefined}
                        dropdownMatchSelectWidth={false}
                         virtual={true}
                        options={state.startRepOptions}
                        notFoundContent={state.isLoadingAgents ? (
                          <div className="d-flex justify-content-center align-items-center py-2" style={{ color: '#198754' }}>
                            <span className="spinner-border spinner-border-sm text-success me-2" role="status" />
                            Loading agents…
                          </div>
                        ) : null}
                      />
                    ) : (
                      <div className="field-value">
                        <b>{merchant.Sales_Agent || "-"}</b>
                      </div>
                    )}
                  </div>
                )}

                <div className="col-md-2 col-6 mb-3">
                  <label className="field-label">Gateway ID</label>
                  {state.isEditingAppInfo ? (
                    <Input
                      size="large"
                      name="gatewayId"
                      value={state.gatewayId}
                      onChange={handleChange}
                      placeholder="Enter Gateway ID"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{merchant.Gateway_ID || "-"}</b>
                    </div>
                  )}
                </div>

                {/* Start Rep field - editable in edit mode */}
                <div className="col-md-3 col-6 mb-3">
                  <label className="field-label">Start Rep</label>
                  {state.isEditingAppInfo ? (
                    <Input
                      size="large"
                      name="startRep"
                      value={state.startRep}
                      onChange={handleChange}
                      placeholder="Enter Start Rep"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{merchant.Account_Rep || "-"}</b>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Merchant Info Section */}
            <div className="info-section mb-4">
              <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                <h5 className="section-title mb-0">
                  Merchant Info
                  {!state.isEditingMerchantInfo && canEditLead && (
                    <SimpleTooltip text="Click to edit Merchant Info">
                      <img
                        src={iconEditGreen}
                        alt="edit"
                        className="edit-icon ms-2 icon-button-pointer"
                        onClick={toggleMerchantInfoEdit}
                      />
                    </SimpleTooltip>
                  )}
                </h5>
                {state.isEditingMerchantInfo && (
                  <div className="d-flex gap-2">
                    <SimpleTooltip text="Save Changes">
                      <img
                        src={iconSaveGreen}
                        alt="save"
                        onClick={saveMerchantInfo}
                        className={`icon-button-medium ${state.isLoadingAccountExec ? 'icon-button-disabled' : 'icon-button-pointer'}`}
                      />
                    </SimpleTooltip>
                    <SimpleTooltip text="Cancel">
                      <img
                        src={iconCancelRed}
                        alt="cancel"
                        onClick={state.isLoadingAccountExec ? null : toggleMerchantInfoEdit}
                        className={`icon-button-medium ${state.isLoadingAccountExec ? 'icon-button-disabled' : 'icon-button-pointer'}`}
                      />
                    </SimpleTooltip>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-2 col-6 mb-3">
                  <label className="field-label">Merchant Name</label>
                  {state.isEditingMerchantInfo ? (
                    <Input
                      size="large"
                      name="merchantName"
                      value={state.merchantName}
                      onChange={handleChange}
                      placeholder="Enter Merchant Name"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{merchant.Full_Name || "Jerry Robinson"}</b>
                    </div>
                  )}
                </div>
                <div className="col-md-2 col-6 mb-3">
                  <label className="field-label">Merchant Phone</label>
                  {state.isEditingMerchantInfo ? (
                    <Input
                      size="large"
                      type="tel"
                      name="merchantPhone"
                      value={state.merchantPhone}
                      onChange={handleChange}
                      placeholder="Enter Phone Number"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{merchant.Phone || "801.254.5555"}</b>
                    </div>
                  )}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="field-label">Merchant Email</label>
                  {state.isEditingMerchantInfo ? (
                    <Input
                      size="large"
                      type="email"
                      name="merchantEmail"
                      value={state.merchantEmail}
                      onChange={handleChange}
                      placeholder="Enter Email Address"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{merchant.Email || "email@merchant.com"}</b>
                    </div>
                  )}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="field-label">Merchant Address</label>
                  {state.isEditingMerchantInfo ? (
                    <Input
                      size="large"
                      name="merchantAddress"
                      value={state.merchantAddress || ""}
                      onChange={handleChange}
                      placeholder="Enter Address"
                    />
                  ) : (
                    <div className="field-value">
                      <b>{getMerchantAddress() || "-"}</b>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notes Section */}
            <div className="info-section mb-3">
              <div className="section-header mb-3 d-flex justify-content-between align-items-center">
                <h5 className="section-title mb-0">
                  Notes
                  {!state.isAddingNote && canEditLead && (
                    <SimpleTooltip text="Click to add new note">
                      <img
                        src={iconAddNotes}
                        alt="add notes"
                        className="edit-icon ms-2 icon-button-small icon-button-pointer"
                        onClick={toggleAddNote}
                      />
                    </SimpleTooltip>
                  )}
                </h5>
                {state.isAddingNote && (
                  <div className="d-flex gap-2">
                    <SimpleTooltip text={state.isLoadingAccountExec ? "Saving..." : "Save Note"}>
                      {state.isLoadingAccountExec ? (
                        <div className="icon-button-medium d-flex align-items-center justify-content-center">
                          <div className="spinner-border spinner-border-sm text-success" role="status" style={{width: '1rem', height: '1rem'}}>
                            <span className="visually-hidden">Saving...</span>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={iconSaveGreen}
                          alt="save"
                          onClick={saveNote}
                          className="icon-button-medium icon-button-pointer"
                        />
                      )}
                    </SimpleTooltip>
                    <SimpleTooltip text="Cancel">
                      <img
                        src={iconCancelRed}
                        alt="cancel"
                        onClick={state.isLoadingAccountExec ? null : toggleAddNote}
                        className={`icon-button-medium ${state.isLoadingAccountExec ? 'icon-button-disabled' : 'icon-button-pointer'}`}
                      />
                    </SimpleTooltip>
                  </div>
                )}
              </div>
              
              {state.isAddingNote && (
                <div className="mb-3">
                  <textarea
                    className="form-control"
                    name="newNote"
                    value={state.newNote}
                    onChange={handleChange}
                    placeholder="  Enter your note here..."
                    rows="4"
                    disabled={state.isLoadingAccountExec}
                  />
                </div>
              )}
              
              <div className="notes-content">
                {merchant.Notes1
                  ? merchant.Notes1.split("\n").map((line, i) => (
                      <div key={i} className="note-line mb-2">
                        <b>{line}</b>
                      </div>
                    ))
                  : <div className="note-line"><b>No notes available</b></div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(BusinessModal);